import { ElementType } from 'react';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ArticleIcon from '@mui/icons-material/Article';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import DatasetIcon from '@mui/icons-material/Dataset';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import PsychologyIcon from '@mui/icons-material/Psychology';
import SearchIcon from '@mui/icons-material/Search';
import StormIcon from '@mui/icons-material/Storm';
import TableChartIcon from '@mui/icons-material/TableChart';

export type Suggestion = {
  id: number;
  icon?: ElementType;
  label: string;
  prompt: string;
};

export const MULTI_TOOL_SUGGESTIONS: Suggestion[] = [
  {
    id: 1,
    icon: SearchIcon,
    label: 'Find targeted papers',
    prompt:
      "Point me to Mike D'Arcy's most-cited paper on building an automated LLM-based paper reviewing system",
  },
  {
    id: 2,
    icon: ContactSupportIcon,
    label: 'Ask about a paper',
    prompt:
      'What are the training and eval guidelines assessed in the paper "Paloma: A Benchmark for Evaluating Language Model Fit" (corpusId: 266348815)? Show relevant excerpts so I can verify.',
  },
  {
    id: 3,
    icon: TableChartIcon,
    label: 'Compare papers',
    prompt:
      "I'm interested in understanding how prior work on predicting success or failure outcomes of clinical trials design the prediction task. I have identified the following relevant papers:\n\n- SPOT: Sequential Predictive Modeling of Clinical Trial Outcome with Meta-Learning (corpus ID: 258059679);\n- Automatically Labeling $200B Life-Saving Datasets: A Large Clinical Trial Outcome Benchmark (corpus ID: 270559984);\n- Predicting Clinical Trial Results by Implicit Evidence Integration (corpus ID: 222291457);\n- Predicting Intervention Approval in Clinical Trials through Multi-Document Summarization (corpus ID: 247922360);\n- Improving the Prediction of Clinical Success Using Machine Learning (corpus ID: 231781240).\n\nCan you add these papers to a comparison table with suggested aspects covering various task/dataset design choices such as input features, output format, etc.?",
  },
  {
    id: 4,
    icon: DatasetIcon,
    label: 'Analyze data',
    prompt:
      'Can you analyze the relation between time preference and BMI in this dataset\n\nAttachments\n\n• s3://ai2-asta-workspaces/sampledata/nls_bmi_processed.csv',
  },
  {
    id: 5,
    icon: ArticleIcon,
    label: 'Ask questions across scientific literature',
    prompt: 'What AI work has been done in answering science questions?',
  },
  {
    id: 6,
    icon: SearchIcon,
    label: 'Run a challenging paper search',
    prompt:
      'Find papers that inject ummmms and uhhhs to make speech more human and evaluate the effect',
  },
];

export const CORPUS_QA_SUGGESTIONS: Suggestion[] = [
  {
    id: 1,
    label: 'Compare two theories',
    prompt:
      'What are the differences and similarity between information foraging theory and exploratory search?',
  },
  {
    id: 2,
    label: 'Pretraining LLMs to code',
    prompt:
      "I'm pretraining a language model to help users programming. Can you find good principles for selecting good code documents to pretrain on from a large code repository? for example, do the papers use any heuristics to filter content? do they follow any other principle? do they use models? what other ways I missed on how data should be selected and prepared for language model pretraining? Be thorough.",
  },
  {
    id: 3,
    label: 'Survey LLM ideation support systems',
    prompt:
      'Survey papers on interactive systems that use LLMs to support research ideation.',
  },
  {
    id: 4,
    label: 'Non-linear UIs for LLMs',
    prompt:
      'What are some non-linear user interfaces for interacting with LLMs?',
  },
  {
    id: 5,
    label: 'Pretraining 30B LLMs with GQA',
    prompt:
      'I’m pretraining a large language model (LLM) with over 30B parameters. Should I use grouped query attention (GQA?)',
  },
];

export const PAPER_FINDER_SUGGESTIONS: Suggestion[] = [
  {
    id: 1,
    icon: SearchIcon,
    label: 'Generative document retrieval models that …',
    prompt:
      'Research on generative document retrieval models that allow for quick addition of new documents as they become available',
  },
  {
    id: 2,
    icon: SearchIcon,
    label: 'Papers introducing a dataset of …',
    prompt:
      'Papers introducing a dataset of an unscripted dialogue between 2 speakers (written or transcription) in English where there is an annotation of some property (emotion, age, gender etc..) of one of the speakers.',
  },
  {
    id: 3,
    icon: SearchIcon,
    label: 'Shallow marine ecosystem classification using …',
    prompt:
      'Papers about shallow marine ecosystem classification using hyperspectral imagery',
  },
  {
    id: 4,
    icon: SearchIcon,
    label: 'Long term memory in LLM agents',
    prompt: 'Papers about long term memory in LLM agents',
  },
  {
    id: 5,
    icon: SearchIcon,
    label: 'Papers by Dan Weld about planning',
    prompt: 'Papers by Dan Weld about planning',
  },
  {
    id: 6,
    icon: SearchIcon,
    label: 'The Brown clustering paper',
    prompt: 'The Brown clustering paper',
  },
];

export const TABLE_ANNOTATION_SUGGESTIONS: Suggestion[] = [];

export const DATAVOYAGER_SUGGESTIONS: Suggestion[] = [
  {
    id: 1,
    icon: DirectionsBoatIcon,
    label: 'Titanic survival factors (auto analysis)',
    prompt: `Out of age and gender, which factor affects the survival of titanic passengers the most?

Attachments

• s3://ai2-asta-workspaces/sampledata/titanic.csv`,
  },
  {
    id: 2,
    icon: AccountBalanceIcon,
    label: 'NLS savings behavior (query suggestions)',
    prompt: `Study savings behaviors in this dataset.

Attachments

• s3://ai2-asta-workspaces/sampledata/nls_bmi_processed.csv`,
  },
  {
    id: 3,
    icon: PsychologyIcon,
    label: "Thal phase and Alzheimer's (auto analysis)",
    prompt: `What is the relationship between Thal phase and Overall Alzheimer's Disease Neuropathological Change (ADNC) scores in the cohort? Both of these variables are categorical, so perform analysis and tests accordingly.

Attachments

• s3://ai2-asta-workspaces/sampledata/SEA-AD_Cohort_Metadata.csv`,
  },
  {
    id: 4,
    icon: StormIcon,
    label: 'Hurricane fatalities (query suggestions)',
    prompt: `The dataset includes archival data on fatalities caused by hurricanes in the United States (1950-2012). Ninety-four Atlantic hurricanes made landfall in the United States during this period. Here are the column descriptions:

- ind: Unique identifier for each row
- year: Year the hurricane occurred
- name: Name of the hurricane
- masfem: Nine independent coders who were blind to the hypothesis rated the masculinity vs. femininity of historical hurricane names on two items (1=very masculine, 11=very feminine, and 1=very man-like, 11=very woman-like), which were averaged to compute a masculinity-femininity index.
- min: Minimum pressure of the hurricane at the time of landfall in the United States from the NOAA
- gender_mf: Binary gender indicator of the hurricane name (0 for male, 1 for female)
- category: Category of the hurricane on the Saffir-Simpson scale
- alldeaths: Total number of deaths caused by the hurricane
- ndam: Raw dollar amounts of property damage caused by hurricanes, normalized to 2013 monetary values by adjusting for inflation, wealth, and population density.
- elapsedyrs: Number of years elapsed since the hurricane
- source: Source of the hurricane data
- masfem_mturk: An MTurk survey with 32 participants asking them to rate all 94 storms using the same scale as the 'masfem' column
- wind: Maximum wind speed of the hurricane at the time of landfall in the United States from the NOAA
- ndam15: Raw dollar amounts of property damage caused by hurricanes, normalized to 2015 monetary values by adjusting for inflation, wealth, and population density.

What are some interesting analyses you can do on this dataset?

Attachments

• s3://ai2-asta-workspaces/sampledata/hurricane.csv`,
  },
];
